/**
 * Accent colours — the one hue a person gets to choose.
 *
 * Every app in this product ships a MONOCHROME primary: black on light, white
 * on dark. That is the house style and it stays the default, because a till,
 * a signage console and an admin panel used side by side should not each shout
 * a different colour at whoever is looking after all three. But the primary is
 * also the only saturated thing on the screen, so it is the one knob a person
 * can turn without being able to make the product unreadable — which is
 * exactly what a preference should be.
 *
 * ── Why presets rather than a colour wheel ──────────────────────────────────
 * A free picker lets someone choose a primary that their own text cannot be
 * read against. Each preset here carries its OWN foreground, chosen from the
 * measured contrast rather than assumed: the yellow and the green take dark
 * type (10.9:1 and 8.1:1), the violet, blue and red take white (4.7:1 each).
 * Two of the hues were darkened a few points of lightness from the swatch they
 * were sampled from for precisely that reason — at their original lightness
 * white type on them sat around 3.8:1, which is below the 4.5:1 floor.
 *
 * Values are bare `H S% L%` triples, not `hsl(...)`, because that is the shape
 * Tailwind's `hsl(var(--primary))` expects.
 */

export type AccentKey = 'mono' | 'violet' | 'azure' | 'amber' | 'emerald' | 'crimson'

export interface Accent {
  key: AccentKey
  label: string
  /** `--primary` / `--ring`, as `H S% L%`. `null` = leave the stylesheet alone. */
  primary: string | null
  /** `--primary-foreground`, as `H S% L%`. */
  foreground: string | null
  /** The colour of the swatch in the picker — for `mono`, the theme's own ink. */
  swatch: string
}

export const ACCENTS: Accent[] = [
  {
    key: 'mono',
    label: 'Monochrome',
    // Nothing is set: the app's own stylesheet keeps its black/white primary,
    // including its dark-mode variant. That is why this is null rather than a
    // hard-coded black — a fixed value would break dark mode.
    primary: null,
    foreground: null,
    swatch: 'hsl(var(--foreground))',
  },
  { key: 'violet',  label: 'Violet',  primary: '278 60% 56%', foreground: '0 0% 100%', swatch: 'hsl(278 60% 56%)' },
  { key: 'azure',   label: 'Azure',   primary: '216 94% 51%', foreground: '0 0% 100%', swatch: 'hsl(216 94% 51%)' },
  { key: 'amber',   label: 'Amber',   primary: '44 90% 60%',  foreground: '0 0% 9%',   swatch: 'hsl(44 90% 60%)' },
  { key: 'emerald', label: 'Emerald', primary: '135 59% 49%', foreground: '0 0% 9%',   swatch: 'hsl(135 59% 49%)' },
  { key: 'crimson', label: 'Crimson', primary: '4 86% 48%',   foreground: '0 0% 100%', swatch: 'hsl(4 86% 48%)' },
]

export const DEFAULT_ACCENT: AccentKey = 'mono'

/**
 * Every custom property the accent owns.
 *
 * Listed once so that clearing is the exact inverse of setting. When the two
 * were written out by hand they drifted: the setter grew `--ring` and the
 * sidebar pair while the clearer still removed two properties, which left
 * "Monochrome" unable to fully undo a colour.
 */
const ACCENT_PROPS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-ring',
] as const

export const findAccent = (key: string | null | undefined): Accent =>
  ACCENTS.find(a => a.key === key) ?? ACCENTS[0]

/**
 * Write the accent onto `<html>`, or clear it.
 *
 * Inline properties on the root element out-rank every selector in the
 * stylesheet, including the `.dark` block — which is what makes "mono" a
 * REMOVAL rather than a value. Set a black there instead and dark mode would
 * be stuck with a black primary on a black surface.
 */
export function applyAccent(key: string | null | undefined): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const accent = findAccent(key)
  if (!accent.primary || !accent.foreground) {
    for (const prop of ACCENT_PROPS) root.style.removeProperty(prop)
    return
  }
  root.style.setProperty('--primary', accent.primary)
  root.style.setProperty('--primary-foreground', accent.foreground)
  // The focus ring follows the accent: it is the same "this is the active
  // thing" signal, and leaving it black while buttons turned violet reads as
  // two different design systems in one screen.
  root.style.setProperty('--ring', accent.primary)
  // The sidebar keeps its OWN copy of the primary tokens (--sidebar-primary is
  // the selected-nav pill, --sidebar-ring its focus ring). They were left out
  // of this function, so choosing violet turned every button violet and left
  // the one item the eye goes to first — the page you are on — brand black.
  // Same hue, same measured foreground: the pair has to move together or the
  // accent looks half-applied, which is exactly how it read.
  root.style.setProperty('--sidebar-primary', accent.primary)
  root.style.setProperty('--sidebar-primary-foreground', accent.foreground)
  root.style.setProperty('--sidebar-ring', accent.primary)
}
